import { useEffect, useState } from 'react'

import { useQueryGetBookingById } from '@flecto/core/saas'
import { type CoreProductSelection, type ParameterID } from '@flecto/types'

import { useBookingItemAvailabilityStatus } from '../../hooks/useBookingItemAvailabilityStatus'

export type UseBookingItemAvailabilityOptions = {
  bookingId?: ParameterID
  quantityStart?: number
  productSelected?: CoreProductSelection
}

export function useItemsAvailabilityCalculator({
  quantityStart = 0,
  bookingId,
  productSelected,
}: UseBookingItemAvailabilityOptions) {
  const [quantity, setQuantity] = useState(quantityStart)

  const [remainingQuantity, setRemainingQuantity] = useState(0)
  const [originalQuantity] = useState(quantityStart)
  const [originalRemainingQuantity, setOriginalRemainingQuantity] = useState(0)

  const { data: booking, isLoading: isLoadingBooking } = useQueryGetBookingById(
    { bookingId }
  )

  const { isUnavailable, isOverbooked } = useBookingItemAvailabilityStatus({
    item: productSelected?.product?.item_attributes,
    remainingQuantity,
  })

  const updateQuantity = (value: number, isDelta = false) => {
    if (value === 0) {
      setQuantity(value)
      setRemainingQuantity(originalRemainingQuantity)
    }

    if (isDelta) {
      setQuantity((prevQuantity) => prevQuantity + value)
      setRemainingQuantity((prevRemaining) => prevRemaining - value)
    } else {
      setQuantity(value)
      setRemainingQuantity(() => originalRemainingQuantity - value)
    }
  }

  const increaseQuantity = () => updateQuantity(1, true)
  const decreaseQuantity = () => updateQuantity(-1, true)

  useEffect(() => {
    if (productSelected && booking) {
      const itemAvailabilityRemainingQty =
        productSelected.remaining_quantity ?? 0

      const remainingQuantity = ['drafting', 'negotiating'].includes(
        booking.status
      )
        ? itemAvailabilityRemainingQty - originalQuantity
        : itemAvailabilityRemainingQty

      setRemainingQuantity(remainingQuantity)
      setOriginalRemainingQuantity(remainingQuantity)
    }
  }, [booking])

  const isInfinite = productSelected?.product?.item_attributes?.track_quantity
    ? false
    : true

  const infiniteSymbol = '∞'

  const colorAvailability = isUnavailable
    ? 'red'
    : isOverbooked
      ? 'orange'
      : 'gray'

  const inputMaxQuantity =
    productSelected?.product?.item_attributes?.track_quantity &&
    !productSelected?.product.item_attributes?.allow_overbooking
      ? originalRemainingQuantity + originalQuantity
      : undefined

  return {
    isLoading: isLoadingBooking,
    isInfinite,
    isOverbooked,
    isUnavailable,
    colorAvailability,
    infiniteSymbol,
    inputMaxQuantity,
    quantity,
    remainingQuantity,
    originalRemainingQuantity,
    originalQuantity,
    increaseQuantity,
    decreaseQuantity,
    updateQuantity,
  }
}
